/**
 * BusinessActivity — vertical list of the 7 businesses with their
 * current day's reward + a subtle progress bar.
 *
 * Each row uses a per-business hue so the user builds an intuitive
 * association ('the cyan one is VPN'). The bars are static-looking
 * but the values come from a live API hook in the page that uses
 * this component.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { LiquidNumber } from '@/components/glass';

export interface BusinessRow {
  name: string;
  short: string;
  reward: number;
  share: number; // 0..1, how much of total today
  hue: number;
}

interface BusinessActivityProps {
  businesses: BusinessRow[];
  className?: string;
}

export function BusinessActivity({ businesses, className }: BusinessActivityProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {businesses.map((b) => (
        <div key={b.short} className="group">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-mono font-semibold"
                style={{
                  background: `hsl(${b.hue} 70% 60% / 0.16)`,
                  color: `hsl(${b.hue} 80% 70%)`,
                  boxShadow: `inset 0 0 0 1px hsl(${b.hue} 80% 65% / 0.4)`,
                }}
              >
                {b.short}
              </span>
              <span className="text-[13px] text-[var(--text-secondary)]">
                {t(`dashboard.biz.${b.short}`, { defaultValue: b.name })}
              </span>
            </div>
            <LiquidNumber
              value={b.reward}
              decimals={3}
              suffix="USD₮"
              className="text-[13px] text-[var(--text-primary)]"
            />
          </div>
          <div className="relative h-1 rounded-full bg-[var(--glass-fill)] overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-[var(--ease-liquid)]"
              style={{
                width: `${Math.max(2, Math.min(100, b.share * 100))}%`,
                background: `linear-gradient(90deg, hsl(${b.hue} 80% 60%), hsl(${b.hue} 80% 70%))`,
                boxShadow: `0 0 12px hsl(${b.hue} 80% 60% / 0.5)`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
